/**
 * Mise en évidence de la saisie dans une suggestion.
 *
 * Reprend la règle d'`autosuggest-highlight`, que l'autocomplétion utilisait : chaque mot de la
 * saisie est cherché en **début de mot** dans le texte, sans tenir compte de la casse ni des
 * accents, à sa première occurrence.
 */

const fold = (value: string) => value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

const isWordChar = (char: string | undefined) => !!char && /[\p{L}\p{N}]/u.test(char);

/** Plages `[début, fin[` du texte couvertes par les mots de la saisie. */
export const matchRanges = (text: string, query: string): [number, number][] => {
    // Le repliement des accents conserve la longueur pour les lettres latines usuelles :
    // les index restent alignés sur le texte d'origine.
    const folded = fold(text);
    const ranges: [number, number][] = [];

    for (const word of fold(query).trim().split(/\s+/).filter(Boolean)) {
        let from = 0;
        while (from <= folded.length - word.length) {
            const index = folded.indexOf(word, from);
            if (index === -1) break;

            const end = index + word.length;
            const atWordStart = !isWordChar(folded[index - 1]);
            const overlaps = ranges.some(([s, e]) => index < e && end > s);

            if (atWordStart && !overlaps) {
                ranges.push([index, end]);
                break;
            }
            from = index + 1;
        }
    }

    return ranges.sort((a, b) => a[0] - b[0]);
};

/** Découpe le texte en segments, mis en évidence ou non. */
export const parseHighlight = (text: string, ranges: [number, number][]): { text: string; highlight: boolean }[] => {
    if (ranges.length === 0) return [{ text, highlight: false }];

    const parts: { text: string; highlight: boolean }[] = [];
    let cursor = 0;

    for (const [start, end] of ranges) {
        if (start > cursor) parts.push({ text: text.slice(cursor, start), highlight: false });
        parts.push({ text: text.slice(start, end), highlight: true });
        cursor = end;
    }

    if (cursor < text.length) parts.push({ text: text.slice(cursor), highlight: false });

    return parts;
};
